// Fiche d'une demande côté usager (écran S21) : encart d'état avec l'étape sur sept et ce qui est
// attendu, chemin vertical, pièces et verdicts, décision, documents délivrés avec code de
// vérification, conversation avec le service (nombre de messages et non-lus lus dans la fiche, sans
// marquer lu), complément et retrait. Jamais de note interne : l'API usager ne les sert pas (R15).
import { Bandeau, Bouton, ConfirmationIntegree, LienBouton, ZoneTexte } from '@cc/ui';
import { Download, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date, dateHeure } from '../../../commun/format';
import { useMessages } from '../../../commun/Messages';
import { ouvrirDocument, useDossierUsager } from '../api';
import { Bloc, EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { dernierMessage, libelleNonLus, resumeConversation } from '../conversation';
import { useDepot } from '../depot/useDepot';
import { LIBELLES_VERDICT } from '../pieces';
import { rangStatut, texteEtat } from '../statuts';
import type { FicheDossierUsager } from '../types';
import { BarresEtapes, CheminDossier } from './CheminDossier';
import commun from '../citoyen.module.css';

const LIBELLES_RESULTAT: Record<string, string> = { approuve: 'Approuvée', approuve_sous_conditions: 'Approuvée sous conditions', refuse: 'Refusée' };

function EncartEtat({ d }: { d: FicheDossierUsager }) {
  const rang = rangStatut(d.statut.code);
  const etat = texteEtat(d);
  const brouillon = d.statut.code === 'brouillon';
  return (
    <Bloc etiquette={brouillon ? 'Brouillon non déposé' : `Étape ${rang} sur 7`} titre={d.statut.libelle}>
      {!brouillon ? <BarresEtapes statut={d.statut.code} /> : null}
      <p className={commun.blocTexte}>
        {etat.texte} {!etat.action && !brouillon ? <strong>Vous n&apos;avez rien à faire.</strong> : null}
      </p>
      {d.complement?.motif && d.statut.code === 'incomplet' ? <p className={commun.blocTexte}>Motif : {d.complement.motif}</p> : null}
      {brouillon ? (
        <LienBouton href={`/citoyen/${d.collectivite.code}/demarches/${d.demarche.code}/deposer?dossier=${d.id}`} variante="principal">
          Reprendre la demande
        </LienBouton>
      ) : d.actions_possibles.includes('completer') ? (
        <LienBouton href={`/citoyen/mes-dossiers/${d.id}/complement`} variante="principal">
          Fournir le complément
        </LienBouton>
      ) : null}
    </Bloc>
  );
}

function Documents({ d }: { d: FicheDossierUsager }) {
  const [erreur, setErreur] = useState<string | null>(null);
  if (d.documents_delivres.length === 0) return null;
  return (
    <Section titre="Documents délivrés">
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <Liste libelle="Documents délivrés">
        {d.documents_delivres.map((doc) => (
          <li key={doc.id} className={`${commun.ligne} ${commun.ligneSansChevron}`}>
            <span className={commun.ligneTexte}>
              <span className={commun.ligneTitre}>{doc.titre}</span>
              <span className={commun.ligneSous}>
                Délivré le {date(doc.cree_le)}
                {doc.code_verification ? (
                  <>
                    {' · code '}
                    <Link to={`/citoyen/verification/${doc.code_verification}`} className={commun.mono}>
                      {doc.code_verification}
                    </Link>
                  </>
                ) : null}
              </span>
            </span>
            <Bouton variante="secondaire" petit icone={Download} onClick={() => ouvrirDocument(`/usager/dossiers/${d.id}/documents/${doc.id}`).catch((e: unknown) => setErreur(messageErreur(e)))}>
              Télécharger
            </Bouton>
          </li>
        ))}
      </Liste>
    </Section>
  );
}

function Retrait({ d }: { d: FicheDossierUsager }) {
  const depot = useDepot(d.id);
  const { afficher } = useMessages();
  const [motif, setMotif] = useState('');
  if (!d.actions_possibles.includes('retirer')) return null;
  return (
    <section className={commun.pile} aria-label="Retirer la demande">
      <p className={commun.aide}>Vous pouvez retirer cette demande tant qu&apos;aucune décision n&apos;est prise. Les notes internes du service ne vous sont jamais montrées ; les messages, si.</p>
      {depot.messageErreur ? <Bandeau type="erreur">{depot.messageErreur}</Bandeau> : null}
      <ConfirmationIntegree
        libelle="Retirer la demande"
        question="Retirer définitivement cette demande ? Elle ne sera plus traitée ; vous pourrez en déposer une nouvelle."
        libelleConfirmer="Confirmer le retrait"
        enCours={depot.enCours === 'retirer'}
        confirmationDesactivee={motif.trim().length < 3}
        onConfirmer={() => void depot.retirer(motif.trim()).then(() => afficher('succes', 'Demande retirée.'))}
      >
        <ZoneTexte libelle="Motif du retrait" value={motif} onChange={(e) => setMotif(e.target.value)} required rows={2} />
      </ConfirmationIntegree>
    </section>
  );
}

function Fiche({ d }: { d: FicheDossierUsager }) {
  const conversation = d.conversation;
  const dernier = dernierMessage(conversation);
  const conformes = d.pieces.filter((p) => p.verdict === 'conforme').length;
  const nonConformes = d.pieces.filter((p) => p.verdict === 'non_conforme').length;
  return (
    <>
      <EncartEtat d={d} />
      {d.resultat ? (
        <section className={commun.carte} aria-labelledby="decision-titre">
          <h2 id="decision-titre">Décision : {LIBELLES_RESULTAT[d.resultat] ?? d.resultat}</h2>
          {d.motif_decision ? <p>{d.motif_decision}</p> : <p className={commun.aide}>Aucun motif n&apos;a été communiqué.</p>}
        </section>
      ) : null}
      <Documents d={d} />
      {d.statut.code !== 'brouillon' ? (
        <Section titre="Le chemin de votre demande">
          <CheminDossier dossier={d} />
        </Section>
      ) : null}
      <Section titre="Pièces et messages">
        <Liste>
          <li className={`${commun.ligne} ${commun.ligneSansChevron}`}>
            <span className={commun.ligneTexte}>
              <span className={commun.ligneTitre}>Pièces</span>
              <span className={commun.ligneSous}>
                {d.pieces.length} fournie{d.pieces.length > 1 ? 's' : ''} sur {d.pieces_attendues.length} attendue{d.pieces_attendues.length > 1 ? 's' : ''}
              </span>
            </span>
            <TexteStatut libelle={nonConformes > 0 ? `${nonConformes} non conforme${nonConformes > 1 ? 's' : ''}` : conformes === d.pieces.length && d.pieces.length > 0 ? 'Toutes conformes' : 'En examen'} ton={nonConformes > 0 ? 'attention' : conformes === d.pieces.length && d.pieces.length > 0 ? 'ok' : 'neutre'} />
          </li>
          {d.pieces.map((p) => (
            <li key={p.id} className={`${commun.ligne} ${commun.ligneSansChevron}`}>
              <span className={commun.ligneTexte}>
                <span className={commun.ligneTitre}>{p.titre}</span>
                {p.motif ? <span className={commun.ligneSous}>{p.motif}</span> : null}
              </span>
              <TexteStatut libelle={LIBELLES_VERDICT[p.verdict]?.libelle ?? p.verdict} ton={LIBELLES_VERDICT[p.verdict]?.ton ?? 'neutre'} />
            </li>
          ))}
          {d.pieces_attendues
            .filter((p) => !p.fournie)
            .map((p) => (
              <li key={p.type} className={`${commun.ligne} ${commun.ligneSansChevron}`}>
                <span className={commun.ligneTexte}>
                  <span className={commun.ligneTitre}>{p.libelle}</span>
                  <span className={commun.ligneSous}>{p.obligatoire ? 'Obligatoire' : 'Facultative'}</span>
                </span>
                <TexteStatut libelle="Non fournie" ton={p.obligatoire ? 'attention' : 'neutre'} />
              </li>
            ))}
          {d.statut.code !== 'brouillon' ? (
            <Ligne
              href={`/citoyen/mes-dossiers/${d.id}/messages`}
              titre="Messages avec le service"
              sousTitre={resumeConversation(conversation)}
              droite={conversation.non_lus > 0 ? <TexteStatut libelle={libelleNonLus(conversation.non_lus)} ton="attention" /> : dernier ? `Dernier le ${date(dernier.horodatage)}` : undefined}
            />
          ) : null}
        </Liste>
      </Section>
      {d.statut.code !== 'brouillon' ? (
        <div className={commun.ligneBoutons}>
          <LienBouton href={`/citoyen/mes-dossiers/${d.id}/messages`} variante="secondaire" icone={MessageSquare}>
            Écrire au service
          </LienBouton>
        </div>
      ) : null}
      <Retrait d={d} />
    </>
  );
}

export function PageDossierUsager() {
  const { id = '' } = useParams();
  const dossier = useDossierUsager(id || null);
  const d = dossier.data;
  return (
    <>
      <EnTeteCitoyen
        titre={d?.demarche.libelle ?? 'Suivi de la demande'}
        surTitre={d ? `${d.representation ? `${d.representation.entite_nom} · ` : ''}${d.collectivite.nom}` : undefined}
        sousTitre={d ? (d.numero ? <span className={commun.mono}>{d.numero}</span> : null) : undefined}
        retour="/citoyen/mes-dossiers"
        libelleRetour="Mes dossiers"
      />
      {d?.enregistre_le ? <p className={commun.aide}>Déposée le {dateHeure(d.enregistre_le)}</p> : null}
      <ExigeConnexion>
        <EtatRequete chargement={dossier.isPending} erreur={dossier.error} onReessayer={() => void dossier.refetch()} fonction="Cette demande" lignes={8}>
          {d ? <Fiche d={d} /> : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
